import { SUBJECT_LABEL } from '@platform/schema/content';
import {
  STEP_COPY,
  goalLimit,
  screenOf,
  stepName,
} from '@platform/schema/onboarding-flow';

import { labelOf } from './controls';

import type { Subject } from '@platform/schema/content';
import type {
  BuiltInStep,
  FlowStep,
  OnboardingConfig,
  OnboardingCopyCatalog,
  OnboardingFlow,
  OnboardingGoal,
  StepArt,
  StepCondition,
} from '@platform/schema/onboarding';
import type { PreviewSample } from '@platform/schema/viewer';

/** A rewording, or the app's own words back when the text is the English again. */
export function reword(
  config: OnboardingConfig,
  key: string,
  text: string,
  english: string,
): OnboardingConfig {
  const rest = config.copy.filter((row) => row.key !== key);
  return {
    ...config,
    copy: text === english ? rest : [...rest, { key, text }],
  };
}

export { STEP_COPY, screenOf };

const STEP_SCREENS = new Set(Object.values(STEP_COPY).flat());

/** Words every screen shares — Back, the phase names, the loading line, Home's heading. */
export const isSharedCopy = (key: string): boolean =>
  !STEP_SCREENS.has(screenOf(key));

/** What each built-in step is for, in the console's words. */
export const STEP_PURPOSE: Record<BuiltInStep, string> = {
  WELCOME: 'The first screen: start onboarding, or sign in to an account.',
  THEME:
    'The learner picks light or dark. Continue switches the app to the pick, which stops it following the phone.',
  MEET_LEO: 'LEO introduces what happens next.',
  GOAL: 'The learner picks what they want to achieve. It shapes the suggested topics.',
  INTERESTS: 'The learner picks the topics they want to learn.',
  STARTING_FOCUS:
    'Shown only with several ready interests: which one to start with.',
  WORK_CONTEXT: 'The learner’s role, and an optional job title.',
  FAMILIARITY:
    'Shown only when there is a starting topic: how well the learner knows it.',
  DAILY_PACE: 'How many lessons a day the learner aims for.',
  STREAK_INTRO: 'Introduces the streak goal and the bonus XP it pays.',
  STREAK_GOAL:
    'The learner commits to a 7, 14, 30 or 50 day streak goal. It is set on the account at sign-up, and the first challenge counts as day 1.',
  PATH: 'The path built from the answers, which introduces the first challenge.',
  FIRST_CHALLENGE:
    'The shared challenge: one lesson, counted once for XP and the daily target.',
  COMPLETION: 'The challenge’s result and takeaway.',
  REMINDER: 'A daily reminder time, and the notification permission request.',
  ACCOUNT: 'The required account, before Home.',
};

/**
 * What a step's answers are used for once given, as built today. A line here that names
 * a feature that does not exist reads to an editor as personalization the app does not do.
 */
export const STEP_DATA: Partial<Record<BuiltInStep, readonly string[]>> = {
  THEME: [
    'Saved on this device only, not to the learner’s account, and not synced to their other devices.',
    'The screen starts on the theme the phone shows now. Learners can change it in Settings → Appearance, where System follows the phone again.',
  ],
  STARTING_FOCUS: [
    'The gray tags on a topic are the learner’s goals (Goal tag texts on Your learning goal). A topic gets one for each picked goal that suggests it, so topics that serve the same goal share a tag. “Something else” and “I’m just exploring” never tag a topic.',
    'The tags only label the list. Its order stays the order of Choose your interests, apart from the suggested topic, which is listed first.',
    'The goals behind the tags keep working after onboarding: they decide the order of the learner’s other topics in their path on Home.',
  ],
  // The first feature to read TopicFamiliarity updates these lines in the same change.
  FAMILIARITY: [
    'Saved for the starting topic only, to the learner’s account when they sign up. Changing the starting topic clears it.',
    'It is stored and not used by any feature yet: no lesson, path or suggestion changes with it.',
  ],
  COMPLETION: [
    'Each correct answer in the first challenge adds its topic to the strengths line, in the order the learner answered. The line names at most 3.',
    'With no correct answer the screen shows the general line instead.',
    'The strengths are not saved to the account and not used for suggestions.',
  ],
  REMINDER: [
    'The time is saved to the account. Once the learner allows notifications, the phone schedules a reminder at that time for each of the next 14 days.',
    'The phone redoes the schedule each time the app opens and whenever the time changes. It skips today’s reminder once the daily target is met, and on the day a topic-ready notice is sent instead. With no next lesson on Home, no reminder is scheduled.',
    'If the learner refuses permission, no reminder is scheduled. They can turn it on later in Settings → Learning reminder.',
  ],
  STREAK_GOAL: [
    'A day counts when the learner finishes 1 lesson or 5 different Shorts.',
    'They earn 1 freeze for every 3 days in a row and can hold 2. A freeze covers one missed day.',
    'The bonus is paid once, on the day the goal is reached. Picking a goal pays nothing.',
  ],
};

/** Readable names for the `{{values}}` the app fills in. */
export const VALUE_NAMES: Record<string, string> = {
  topic: 'Chosen topic',
  count: 'Number',
  xp: 'Bonus XP',
  time: 'Reminder time',
  title: 'Lesson title',
  takeaway: 'Takeaway',
  query: 'Search text',
  phase: 'Phase name',
  position: 'Phase number',
  total: 'Number of phases',
  a: 'First topic',
  b: 'Second topic',
  c: 'Third topic',
};

const VALUE = /\{\{\s*([^,}\s]+)[^}]*\}\}/g;

/** The `{{values}}` a text fills, by name. */
export const valuesIn = (text: string): string[] => [
  ...new Set([...text.matchAll(VALUE)].map((match) => match[1] ?? '')),
];

/** What the preview shows for each value, in the step editor's sample line. */
const SAMPLE_VALUES: Record<string, string> = {
  topic: 'Leadership',
  count: '2',
  time: '8:00 AM',
  title: 'Lead your first meeting',
  takeaway: 'Profit is what is left after costs.',
  query: 'pricing',
};

/** What the preview fills a value with. */
export const sampleOf = (name: string): string =>
  SAMPLE_VALUES[name] ?? VALUE_NAMES[name] ?? name;

/** A text with each value filled as the step editor's sample line shows it. */
export const withSamples = (text: string): string =>
  text.replace(VALUE, (_, name: string) => sampleOf(name));

/** Which of the preview learner's interests can start, chosen beside the preview. */
export type TopicsReady = 'ALL' | 'SOME' | 'NONE';

export const TOPICS_READY: Record<TopicsReady, string> = {
  ALL: 'All',
  SOME: 'Some unavailable',
  NONE: 'None',
};

const SAMPLE_GOALS: OnboardingGoal[] = ['LEAD_PEOPLE', 'GROW_BUSINESS'];
const SAMPLE_INTERESTS: Subject[] = ['LEADERSHIP', 'FINANCE', 'MARKETING'];
const UNREADY: Record<TopicsReady, Subject[]> = {
  ALL: [],
  SOME: ['MARKETING'],
  NONE: SAMPLE_INTERESTS,
};

/**
 * The preview's learner: two goals, so topics carry goal tags and one is suggested, and
 * three interests, so Your starting topic shows. Every topic outside `unready` is ready.
 */
export const previewSample = (
  flow: OnboardingFlow,
  ready: TopicsReady,
): PreviewSample => ({
  goals: SAMPLE_GOALS.slice(0, goalLimit(flow)),
  interests: SAMPLE_INTERESTS,
  unready: UNREADY[ready],
});

/** The copy key of a built-in question's answer, in the app's words. */
export const optionKey = (step: FlowStep['id'], id: string): string =>
  step === 'DAILY_PACE'
    ? `pace.detail${id}`
    : `${STEP_COPY[step as BuiltInStep][0] ?? ''}.${id}`;

const newId = (): string =>
  `custom-${Math.random().toString(36).slice(2, 10).padEnd(4, '0')}`;

/** A new added step, after the given position, written to be edited. */
export function addStep(
  flow: OnboardingFlow,
  kind: 'INFO' | 'QUESTION',
  after: number,
): { flow: OnboardingFlow; id: string } {
  const id = newId();
  const step: FlowStep =
    kind === 'INFO'
      ? {
          kind,
          id,
          enabled: true,
          name: 'New information screen',
          title: 'A headline for this screen',
          button: 'Continue',
        }
      : {
          kind,
          id,
          enabled: true,
          name: 'New question',
          title: 'Your question',
          options: [
            { id: 'first', label: 'First answer' },
            { id: 'second', label: 'Second answer' },
          ],
          multiple: false,
          optional: false,
          button: 'Continue',
        };
  const steps = [...flow];
  steps.splice(after + 1, 0, step);
  return { flow: steps, id };
}

/** A copy of an added step under a new id, after the original. */
export function duplicateStep(
  flow: OnboardingFlow,
  id: string,
): { flow: OnboardingFlow; id: string } {
  const at = flow.findIndex((step) => step.id === id);
  const step = flow[at];
  if (step === undefined || step.kind === 'BUILT_IN') return { flow, id };
  const copy = { ...step, id: newId(), name: `${step.name} (copy)` };
  const steps = [...flow];
  steps.splice(at + 1, 0, copy);
  return { flow: steps, id: copy.id };
}

/** The flow with one step replaced by its edited version. */
export const replaceStep = (
  flow: OnboardingFlow,
  step: FlowStep,
): OnboardingFlow => flow.map((one) => (one.id === step.id ? step : one));

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

/** What an added step's learner-facing fields are called in a summary line. */
const ADDED_FIELDS = {
  title: 'headline',
  body: 'text under the headline',
  button: 'button',
  skipLabel: 'skip button',
} as const;

/** Keys a summary line already reports; any other difference is a plain "Edits". */
const REPORTED = new Set<string>([
  'enabled',
  'art',
  'showIf',
  'limit',
  'order',
  'name',
  'options',
  'multiple',
  'optional',
  ...Object.keys(ADDED_FIELDS),
]);

const unreported = (step: FlowStep) =>
  Object.entries(step).filter(([key]) => !REPORTED.has(key));

function artLine(art: StepArt | undefined): string {
  if (art === undefined) return 'back to its own illustration';
  if (art.kind === 'NONE') return 'no illustration';
  const description = art.description?.trim();
  return `a ${art.size} image${description ? ` (“${description}”)` : ''}`;
}

/**
 * What publishing changes, one learner-facing change per line, for an admin to read
 * before agreeing to it. `catalog` gives text keys and answers their readable names.
 */
export function summarize(
  live: OnboardingConfig,
  draft: OnboardingConfig,
  catalog: OnboardingCopyCatalog,
): string[] {
  const english = (key: string) =>
    catalog.entries.find((row) => row.key === key)?.english ?? key;
  const goalName = (goal: OnboardingGoal) =>
    english(goal === 'EXPLORING' ? 'goal.explore' : `goal.${goal}`);
  const topics = (list: readonly Subject[]) =>
    list.map((topic) => SUBJECT_LABEL[topic]).join(', ');
  const who = (condition: StepCondition | undefined) =>
    condition === undefined
      ? 'every learner'
      : condition.answer === 'goal'
        ? `learners whose goals include ${condition.anyOf.map(goalName).join(' or ')}`
        : `learners who chose ${condition.anyOf.map((one) => english(`work.${one}`)).join(' or ')}`;
  const screenName = (key: string) => {
    const id = (Object.keys(STEP_COPY) as BuiltInStep[]).find((one) =>
      STEP_COPY[one].includes(screenOf(key)),
    );
    return id === undefined
      ? 'Shared text'
      : stepName({ kind: 'BUILT_IN', id, enabled: true });
  };

  const lines: string[] = [];
  const liveText = new Map(live.copy.map((row) => [row.key, row.text]));
  const draftText = new Map(draft.copy.map((row) => [row.key, row.text]));
  for (const key of new Set([...liveText.keys(), ...draftText.keys()])) {
    if (liveText.get(key) === draftText.get(key)) continue;
    const text = draftText.get(key);
    lines.push(
      `${screenName(key)} · ${labelOf(key)} ${
        text === undefined
          ? 'goes back to the app’s words'
          : `now reads “${text}”`
      }.`,
    );
  }

  const before = new Map(live.flow.map((step) => [step.id, step]));
  const after = new Map(draft.flow.map((step) => [step.id, step]));
  for (const step of draft.flow) {
    const was = before.get(step.id);
    const name = stepName(step);
    if (was === undefined) {
      lines.push(`Adds “${name}”.`);
      continue;
    }
    const change = (what: string) => lines.push(`${name}: ${what}.`);
    if (was.enabled !== step.enabled) {
      lines.push(`${step.enabled ? 'Turns on' : 'Turns off'} “${name}”.`);
    }
    if (!same(was.art, step.art)) change(`new visual, ${artLine(step.art)}`);
    if (!same(was.showIf, step.showIf)) {
      change(`now shown to ${who(step.showIf)}`);
    }
    if (was.kind === 'BUILT_IN' && step.kind === 'BUILT_IN') {
      const limit = step.limit ?? 3;
      if ((was.limit ?? 3) !== limit) {
        change(
          limit === 1
            ? 'learners can now pick only one goal'
            : `learners can now pick up to ${String(limit)} goals`,
        );
      }
      if (!same(was.order, step.order)) {
        change(
          step.order === undefined
            ? 'answers go back to the app’s order'
            : `answers now show in this order: ${step.order
                .map((id) => english(optionKey(step.id, id)))
                .join(', ')}`,
        );
      }
    }
    if (was.kind !== 'BUILT_IN' && step.kind === was.kind) {
      if (was.name !== step.name) {
        lines.push(`“${was.name}” is renamed “${step.name}” in the console.`);
      }
      for (const [field, label] of Object.entries(ADDED_FIELDS)) {
        const text = (step as Record<string, unknown>)[field];
        if ((was as Record<string, unknown>)[field] === text) continue;
        change(
          typeof text === 'string'
            ? `${label} now reads “${text}”`
            : `${label} is removed`,
        );
      }
      if (was.kind === 'QUESTION' && step.kind === 'QUESTION') {
        if (!same(was.options, step.options)) {
          change(
            `answers are now ${step.options.map((one) => `“${one.label}”`).join(', ')}`,
          );
        }
        if (was.multiple !== step.multiple) {
          change(
            `learners can now pick ${step.multiple ? 'several answers' : 'one answer'}`,
          );
        }
        if (was.optional !== step.optional) {
          change(
            `the question is now ${step.optional ? 'optional' : 'required'}`,
          );
        }
      }
    }
    if (!same(unreported(was), unreported(step))) {
      lines.push(`Edits “${name}”.`);
    }
  }
  for (const step of live.flow) {
    if (!after.has(step.id)) lines.push(`Removes “${stepName(step)}”.`);
  }

  const order = (flow: OnboardingFlow) =>
    flow.filter((step) => after.has(step.id) && before.has(step.id));
  const moved = order(draft.flow).some(
    (step, at) => order(live.flow)[at]?.id !== step.id,
  );
  if (moved) {
    lines.push(
      `New order: ${draft.flow
        .filter((step) => step.enabled)
        .map(stepName)
        .join(' → ')}.`,
    );
  }

  const liveStarts = new Map(live.starts.map((one) => [one.topic, one.next]));
  const draftStarts = new Map(draft.starts.map((one) => [one.topic, one.next]));
  for (const topic of new Set([...liveStarts.keys(), ...draftStarts.keys()])) {
    const next = draftStarts.get(topic);
    if (same(liveStarts.get(topic), next)) continue;
    lines.push(
      `Topic starts · ${SUBJECT_LABEL[topic]} ${
        next === undefined
          ? 'no longer has opening lessons'
          : `now opens with ${next.join(', ')}`
      }.`,
    );
  }

  const liveSuggests = new Map(live.suggestions.map((one) => [one.goal, one]));
  const draftSuggests = new Map(
    draft.suggestions.map((one) => [one.goal, one]),
  );
  for (const goal of new Set([
    ...liveSuggests.keys(),
    ...draftSuggests.keys(),
  ])) {
    const row = draftSuggests.get(goal);
    if (same(liveSuggests.get(goal)?.topics, row?.topics)) continue;
    lines.push(
      `Goal suggestions · “${goalName(goal)}” now suggests ${
        row === undefined ? 'no topics' : topics(row.topics)
      }.`,
    );
  }
  return lines;
}
